import * as React from 'react';
import { DemographicOptionsViewModel, DemographicResponses } from '../../models';
import { Form, Text, Select, FormValues, FormApi, FormErrors } from 'react-form';
import './Demographics.css';
import { Gender, EducationLevel } from '../../enums';
import { ValidationService } from '../../services';
import Datepicker from 'react-datepicker';
import * as moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import * as R from 'ramda';

interface DemographicProps {
    options: DemographicOptionsViewModel;
    handleSubmit: (values: DemographicResponses) => void;
}

interface DemographicState {
    responses: DemographicResponses;
    genderOptions: { label: string, value: Gender }[];
    educationLevelOptions: { label: string, value: EducationLevel }[];
    selectedDate?: moment.Moment;
    datepickerTouched: boolean;
    formErrors: any;
    canSubmit: boolean;
}

export class Demographics extends React.Component<DemographicProps, DemographicState> {
    private labelClass = 'col-sm-3';
    private labelOffset = 'col-sm-offset-3';
    private inputClass = 'col-sm-9';
    private vs = new ValidationService();
    private formApi: FormApi;

    constructor(props: DemographicProps) {
        super(props);

        this.state = {
            formErrors: {},
            responses: {},
            datepickerTouched: false,
            canSubmit: false,
            genderOptions: [
                { label: 'Prefer Not To Answer', value: Gender.preferNotToAnswer },
                { label: 'Female', value: Gender.female },
                { label: 'Male', value: Gender.male }
            ],
            educationLevelOptions: [
                { label: 'Prefer Not To Answer', value: EducationLevel.preferNotToAnswer },
                { label: '8th Grade or Less', value: EducationLevel.eigthGradeOrLess },
                { label: '9th Grade', value: EducationLevel.ninthGrade },
                { label: '10th Grade', value: EducationLevel.tenthGrade },
                { label: '11th Grade', value: EducationLevel.eleventhGrade },
                { label: '12th Grade', value: EducationLevel.twelfthGrade },
                { label: 'College Freshman', value: EducationLevel.collegeFreshman },
                { label: 'College Sophomore', value: EducationLevel.collegeSophomore },
                { label: 'College Junior', value: EducationLevel.collegeJunior },
                { label: 'College Graduate', value: EducationLevel.collegeGraduate },
                { label: `Master's`, value: EducationLevel.masters },
                { label: 'Doctorate', value: EducationLevel.doctorate },
            ]
        };
    }

    private getTextComponent(label: string, property: string, shouldCollect: boolean, isRequired: boolean) {
        if (!shouldCollect) {
            return null;
        }

        if (isRequired) {
            label = `${label} *`;
        }

        return (
            <div className="form-group">
                <label htmlFor={property} className={`${this.labelClass} control-label`}>{label}</label>
                <span className={this.inputClass}>
                    <Text className="form-control" field={property} id={property} />
                    {this.state.formErrors[property] && <span className="help-block error-text">{this.state.formErrors[property]}</span>}
                </span>
            </div>
        );
    }

    private getSelectComponent(label: string, property: string, options: any[], shouldCollect: boolean, isRequired: boolean) {
        if (!shouldCollect) {
            return null;
        }

        if (isRequired) {
            label = `${label} *`;

            // remove the prefernottoanswer option because the property is required
            options = options.filter(x => x.value !== 0);
        }

        return (
            <div className="form-group" >
                <label htmlFor={property} className={`${this.labelClass} control-label`}>{label}</label>
                <span className={this.inputClass}>
                    <Select className="form-control" field={property} id={property} options={options} />
                    {this.state.formErrors[property] && <span className="help-block error-text">{this.state.formErrors[property]}</span>}
                </span>
            </div>
        );
    }

    handleSubmit = (values: any) => {
        if (this.state.selectedDate) {
            values.birthDate = this.state.selectedDate.toDate();
        }

        this.props.handleSubmit(values);
    }

    handleDatepickerOnChange = (newDate: moment.Moment) => {
        this.setState({ selectedDate: newDate }, () => { this.formApi.setTouched('birthDate', true); });
    }

    markDatePickerTouched = () => {
        this.setState({ datepickerTouched: true });
    }

    datePickerBlur = () => {
        this.formApi.setTouched('birthDate', true);
    }

    setFormApi = (api: FormApi) => {
        this.formApi = api;
    }

    validateErrors = (values: FormValues) => {
        const { options } = this.props;
        const { title, city, state, country, gender, educationLevel } = values;
        let formErrors: any = {};

        if (options.collectTitle && options.requireTitle) {
            if (title || title === '') {
                if (!this.vs.required(title)) {
                    formErrors.title = 'Title is required.';
                } else if (!this.vs.cannotBeEmpty(title!)) {
                    formErrors.title = 'Title cannot be blank.';
                }
            }
        }

        if (options.collectBirthPlace && options.requireBirthPlace) {
            if (city || city === '') {
                if (!this.vs.required(city)) {
                    formErrors.city = 'City is required.';
                } else if (!this.vs.cannotBeEmpty(city!)) {
                    formErrors.city = 'City cannot be blank.';
                }
            }

            if (state || state === '') {
                if (!this.vs.required(state)) {
                    formErrors.state = 'State is required.';
                } else if (!this.vs.cannotBeEmpty(state!)) {
                    formErrors.state = 'State cannot be blank.';
                }
            }

            if (country || country === '') {
                if (!this.vs.required(country)) {
                    formErrors.country = 'Country is required.';
                } else if (!this.vs.cannotBeEmpty(country!)) {
                    formErrors.country = 'Country cannot be blank.';
                }
            }
        }

        if (options.collectBirthDate && options.requireBirthDate) {
            if (this.state.datepickerTouched && this.state.selectedDate == null) {
                formErrors.birthDate = 'Birth date is required';
            }
        }

        if (options.collectGender && options.requireGender) {
            if (gender < 1) {
                formErrors.gender = 'Gender is required';
            }
        }

        if (options.collectEducationLevel && options.requireEducationLevel) {
            if (educationLevel < 1) {
                formErrors.educationLevel = 'Education level is required';
            }
        }

        this.setState({ formErrors: formErrors });
        return formErrors;
    }

    validateSuccess = (values: FormValues, errors: FormErrors) => {
        const { options } = this.props;
        const requiredTextFields = [];
        if (options.collectTitle && options.requireTitle) {
            requiredTextFields.push('title');
        }

        if (options.collectBirthPlace && options.requireBirthPlace) {
            requiredTextFields.push('city', 'state', 'country');
        }

        const requiredTextFieldsExist = this.vs.validateRequiredFieldsExist(requiredTextFields, values);

        let requiredSelectsExist = true;
        if (options.requireGender && options.collectGender && (values.gender === undefined || values.gender < 1)) {
            requiredSelectsExist = false;
        }
        if (options.requireEducationLevel && options.collectEducationLevel && (values.educationLevel === undefined || values.educationLevel < 1)) {
            requiredSelectsExist = false;
        }

        let birthdayExists = true;
        if (options.collectBirthDate && options.requireBirthDate && this.state.selectedDate === undefined) {
            birthdayExists = false;
        }

        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && requiredTextFieldsExist && requiredSelectsExist && birthdayExists) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }

    render() {
        const { options } = this.props;
        const { genderOptions, educationLevelOptions } = this.state;

        let titleComponent = this.getTextComponent('Title', 'title', options.collectTitle, options.requireTitle);
        let cityComponent = this.getTextComponent('City', 'city', options.collectBirthPlace, options.requireBirthPlace);
        let stateComponent = this.getTextComponent('State/Province', 'state', options.collectBirthPlace, options.requireBirthPlace);
        let countryComponent = this.getTextComponent('Country', 'country', options.collectBirthPlace, options.requireBirthPlace);

        let genderComponent = this.getSelectComponent('Gender', 'gender', genderOptions, options.collectGender, options.requireGender);
        let educationLevelComponent = this.getSelectComponent('Highest Education Level', 'educationLevel', educationLevelOptions, options.collectEducationLevel, options.requireEducationLevel);

        let birthplaceTitleComponent: React.ReactNode = null;
        if (options.collectBirthPlace) {
            birthplaceTitleComponent = (
                <div className="form-group">
                    <label className={`${this.labelClass} control-label`}>Place of Birth</label>
                </div>
            );
        }

        let birthDateComponent: React.ReactNode = null;
        if (options.collectBirthDate) {
            let birthdatelabel = 'Birth Date';

            if (options.requireBirthDate) {
                birthdatelabel = `${birthdatelabel} *`;
            }

            birthDateComponent = (
                <div className="form-group">
                    <label htmlFor="birthDate" className={`${this.labelClass} control-label`}>{birthdatelabel}</label>
                    <span className={this.inputClass}>
                        <Datepicker
                            selected={this.state.selectedDate}
                            showMonthDropdown
                            showYearDropdown
                            className="form-control"
                            onChange={this.handleDatepickerOnChange}
                            dropdownMode="select"
                            maxDate={moment()}
                            onFocus={this.markDatePickerTouched}
                            onBlur={this.datePickerBlur}
                        />
                        {this.state.formErrors.birthDate && <span className="help-block error-text">{this.state.formErrors.birthDate}</span>}
                    </span>

                </div>
            );
        }

        return (
            <div className="demographics">
                <div className="scale-question-wrapper question">
                    <span>Welcome, {options.firstName}</span>
                    <hr />

                    <h3>Please verify your information</h3>
                    <p><strong>Your demographic information will be used for norming purposes only and will not be disclosed to your sponsoring organization.</strong></p>

                    <div className="container">
                        <Form
                            onSubmit={this.handleSubmit}
                            getApi={this.setFormApi}
                            validateError={this.validateErrors}
                            validateSuccess={this.validateSuccess}
                        >
                            {formApi => (
                                <form onSubmit={formApi.submitForm} className="form-horizontal">
                                    <div className="form-group">
                                        <label className="control-label col-sm-3">Name</label>
                                        <p className="col-sm-9 form-control-static">{options.firstName} {options.lastName}</p>
                                    </div>
                                    <div className="form-group">
                                        <label className="control-label col-sm-3">Organization</label>
                                        <p className="col-sm-9 form-control-static">{options.organizationName}</p>
                                    </div>
                                    {titleComponent}
                                    {genderComponent}
                                    {birthDateComponent}
                                    {birthDateComponent &&
                                        <div className="form-group">
                                            <span className={`${this.labelOffset} ${this.inputClass}`}><strong>Your Birth Date will be used for norming purposes only and will not be disclosed to your sponsoring organization, in compliance with the Age Discrimination in Employment Act of 1967.</strong></span>
                                        </div>
                                    }

                                    {educationLevelComponent}

                                    {birthplaceTitleComponent}
                                    {cityComponent}
                                    {stateComponent}
                                    {countryComponent}
                                    <button type="submit" className="btn btn-primary" disabled={!this.state.canSubmit}>Continue</button>
                                </form>
                            )}
                        </Form>

                    </div>

                    {/* <button onClick={this.submitDemographics} className="btn btn-primary">Take Assessment</button> */}
                    <div style={{ 'clear': 'both' }} />
                </div>
            </div>);
    }
}


// WEBPACK FOOTER //
// ./src/components/assessment/Demographics.tsx